<template>
  <div class="field-wrapper-wuk sword-phone-wuk" :class="{'--bg-fill': fillBg }">
    <slot name="label">
      <input-label
        v-if="label && !labelAsPlaceholder"
        :label="label"
        :style-type="labelStyle"
        :info-text="labelInfo"
        :required="required && showRequiredStar"
        :disabled="disabled"
      />
    </slot>

    <div class="phone-fields-wuk sword-boxed-input-wuk" :class="[inputStateClass, { disabled }]">
      <div v-if="showPrefixDropdown" class="prefix-container">
        <sword-dropdown
          class="prefix-dropdown"
          :options="parsedCountryCodes"
          :disabled="disabled"
          :validation="validation"
          :placeholder="dropdownPlaceholder"
          :search-placeholder="searchPlaceholder"
          :no-results-label="noResultsLabel"
          searchable
          v-model="selectedCountryCode"
          @change="() => (prefixValue = selectedCountryCode)"
          hide-errors
        >
          <dial-code-item
            class="code-display"
            v-if="prefixValue || selectedCountryCode"
            slot="prefix"
            :country-code="selectedCountryCode"
          />
        </sword-dropdown>
      </div>
      <base-input
        v-else
        type="text"
        :name="name"
        :id="uniqueId"
        :placeholder="prefixPlaceholder"
        v-model="prefixValue"
        :required="required"
        :disabled="disabled"
        :validation="validation"
      />
      <text-input
        class="tel-field"
        :name="name"
        :id="uniqueId"
        :label="labelAsPlaceholder ? label : undefined"
        :placeholder="label"
        :label-as-placeholder="labelAsPlaceholder"
        hide-errors
        v-model="phoneValue"
        :mask="mask"
        :required="required"
        :disabled="disabled"
        :validation="validation"
      />
    </div>
    <input-footer-wuk
      v-if="!hideErrors"
      :is-invalid="invalidField"
      :validator="validation"
      :manual-error="manualError"
      :help-text="helpText"
      :success-text="successText"
      :warning-message="warningMessage"
    />
  </div>
</template>

<script>
import countryCodes from '@ui-kit/scripts/definitions/country-codes';
import inputHandler from '@ui-kit/mixins/inputHandler';
import BaseInput from '@ui-kit/components/inputs/BaseInput.vue';
import InputLabel from '@ui-kit/components/inputs/labels/InputLabel.vue';
import TextInput from '@ui-kit/components/inputs/SwordText.vue';
import InputFooterWuk from '@ui-kit/components/inputs/footnotes/InputFooter.vue';
import DialCodeItem from '@ui-kit/components/micro/dialCodeItem.vue';

import validationClasses from '@ui-kit/mixins/inputValidationClasses';
import SwordDropdown from './dropdowns/SwordDropdown.vue';

export default {
  name: 'SwordPhoneWuk',
  mixins: [inputHandler, validationClasses],
  components: {
    BaseInput,
    InputLabel,
    InputFooterWuk,
    SwordDropdown,
    DialCodeItem,
    TextInput,
  },
  props: {
    value: {
      type: [String, Number],
      required: false,
    },
    mask: {
      type: [String],
      default: undefined,
    },
    name: {
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    showPrefixDropdown: {
      type: Boolean,
      default: false,
    },
    dropdownPlaceholder: {
      type: String,
      default: 'Choose',
    },
    searchPlaceholder: {
      type: String,
      default: 'Search',
    },
    prefixPlaceholder: {
      type: String,
      default: '+1',
    },
    phonePlaceholder: {
      type: String,
      default: '000-000-0000',
    },
    country: {
      type: String,
      default: null,
    },
    warningMessage: {
      type: String,
      default: null,
    },
    labelAsPlaceholder: {
      type: Boolean,
      default: false,
    },
    noResultsLabel: {
      type: String,
      default: 'No results for'
    },
  },
  data() {
    return {
      prefixValue: '',
      phoneValue: '',
      selectedCountryCode: '',
      parsedCountryCodes: countryCodes.map(country => ({
        optValue: country.code,
        optLabel: `${country.name} (${country.dialCode})`,
        optDialCode: country.dialCode,
        optIcon: true,
        optSelectedLabel: country.dialCode,
      })),
    };
  },
  watch: {
    country: {
      immediate: true,
      handler() {
        if (this.country) {
          this.selectedCountryCode = this.country;
          this.prefixValue = countryCodes.find(c => c.code === this.country)?.dialCode;
        }
      },
    },
    value: {
      immediate: true,
      handler(val) {
        if (val) {
          const splitter = val.split('//') || [];
          const { 0: prefix, 1: number } = splitter;
          if (this.prefixValue !== prefix) this.prefixValue = prefix;
          if (this.phoneValue !== number) this.phoneValue = number;
          if (this.prefixValue) {
            this.selectedCountryCode = countryCodes.find(c => c.dialCode === this.prefixValue)?.code;
          }
        } else {
          this.selectedCountryCode = '';
        }
      }
    },
    prefixValue(val) {
      if (this.showPrefixDropdown && this.selectedCountryCode) {
        this.$emit('input', this.completePhoneNumber(this.selectedDialCode, this.phoneValue));
      } else {
        this.$emit('input', this.completePhoneNumber(val, this.phoneValue));
      }
    },
    phoneValue(val) {
      this.$emit('input', this.completePhoneNumber(this.prefixValue, val));
    }
  },
  computed: {
    selectedDialCode() {
      return this.parsedCountryCodes.find(
        country => country.optValue === this.selectedCountryCode
      )?.optDialCode;
    },
  },
  methods: {
    completePhoneNumber(prefixVal, numberVal) {
      return prefixVal || numberVal ? `${prefixVal}//${numberVal}` : null;
    },
  },
  mounted() {
    const inputPhone = document.querySelector(`#${this.uniqueId} > input`);

    if (inputPhone) {
      inputPhone.type = 'tel';
    }
  }
};
</script>

<style lang="scss" scoped>
.sword-phone-wuk {
  .sword-boxed-input-wuk {
    overflow: visible;
  }

  .phone-fields-wuk {
    display: flex;
    align-items: stretch;

    &:not(.invalid-field-wuk):not(.disabled) {
      &:focus-within,
      &:hover {
        border-color: $input-border-hover-color-wuk !important;

        .prefix-dropdown::v-deep {
          .dropdown-display-wuk {
            border-color: $input-border-hover-color-wuk !important;
          }
        }

        input:last-child {
          border-color: $input-border-hover-color-wuk !important;
        }
      }
    }

    &:not(.disabled).invalid-field-wuk {
      border-color: $input-error-color !important;

      .prefix-dropdown::v-deep {
        .dropdown-display-wuk {
          border-color: $input-error-color !important;
        }
      }

      input:last-child {
        border-color: $input-error-color !important;
      }
    }

    .prefix-container {
      max-width: min-content;
    }

    .prefix-dropdown {
      &::v-deep {
        flex: none;
        min-width: fit-content;
        position: relative;

        // prefix, we need to change the z-index on hover;
        &:hover,
        &:focus {
          z-index: 10;
        }

        .dropdown-display-wuk {
          box-sizing: border-box;
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
          border: none;
        }

        .dropdown-options-wuk {
          width: 25rem;
          margin-left: -1px;
          border-top-width: 1px;
          border-top-right-radius: $input-radius-wuk !important;
        }

        .code-display {
          height: 4rem;
          padding-left: 1rem;

          span {
            min-width: 1.85rem;
            margin: 0 !important;
          }

          & + input {
            width: 100%;
            min-width: 3rem;
            padding: 1.25rem 0.5rem;
          }
        }

        @media screen and (max-width: $max-smartphone-size) {
          .code-display {
            padding-right: 0;

            & + input {
              min-width: 1.5rem;
            }
          }
        }
      }
    }

    input {
      &:first-child {
        flex: 0 0 15%;
        min-width: calc(4em + #{$input-default-padding-wuk});
        position: relative;
        z-index: 0;
      }
    }

    .tel-field {
      overflow: hidden;
      border: none !important;

      :deep(input) {
        height: 100%;
        border: none !important;
        border-radius: 0;
      }
    }
  }
}
</style>
